import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Link,
  useParams,
  useNavigate,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  FiChevronRight,
  FiHome,
  FiUser,
  FiCreditCard,
  FiLoader,
  FiAlertCircle,
  FiSave,
} from "react-icons/fi";

import toast from "react-hot-toast";

import {
  getUsers,
} from "../../store/slices/profile";

import {
  addSupportIncome,
} from "../../store/slices/withdraw";


const AddSupportIncome = () => {

  const dispatch = useDispatch();

  const { id } = useParams();

  const navigate = useNavigate();


  // =========================================================
  // PROFILE STATE
  // =========================================================

  const {
    users,
    usersLoading,
    error,
  } = useSelector(
    (state) => state.profile
  );


  // =========================================================
  // WITHDRAW / SUPPORT INCOME STATE
  // =========================================================

  const {
    supportIncomeLoading,
    supportIncomeError,
  } = useSelector(
    (state) => state.withdraw
  );


  // =========================================================
  // FORM STATE
  // =========================================================

  const [userId, setUserId] = useState("");

  const [amount, setAmount] = useState("");

  const [validationError, setValidationError] =
    useState("");


  // =========================================================
  // SUBMIT LOADING
  // =========================================================

  const [updating, setUpdating] =
    useState(false);


  // =========================================================
  // PREVENT DUPLICATE API CALL
  // =========================================================

  const fetchedUserId = useRef(null);


  // =========================================================
  // FETCH USER
  // =========================================================

  useEffect(() => {

    if (!id) {
      return;
    }

    // Prevent duplicate API call
    if (fetchedUserId.current === id) {
      return;
    }

    fetchedUserId.current = id;

    dispatch(
      getUsers({
        id: id,
        page: 1,
        limit: 1,
      })
    );

  }, [dispatch, id]);


  // =========================================================
  // SET USER DATA
  // =========================================================

  useEffect(() => {

    if (
      !Array.isArray(users) ||
      users.length === 0
    ) {
      return;
    }


    const user =
      users.find(
        (item) =>
          String(item?._id) === String(id) ||
          String(item?.userId) === String(id)
      ) || users[0];


    if (!user) {
      return;
    }


    console.log(
      "USER RESPONSE:",
      user
    );


    /*
      Support Income API expects:

      userId: actual user ID / user ID
    */

    setUserId(
      user?.userId ||
      user?._id ||
      id ||
      ""
    );

  }, [users, id]);


  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {

    e.preventDefault();

    setValidationError("");


    // =======================================================
    // USER ID VALIDATION
    // =======================================================

    const trimmedUserId =
      String(userId || "").trim();


    if (!trimmedUserId) {

      setValidationError(
        "User ID is missing."
      );

      return;
    }


    // =======================================================
    // AMOUNT VALIDATION
    // =======================================================

    const trimmedAmount =
      String(amount || "").trim();


    if (!trimmedAmount) {

      setValidationError(
        "Please enter amount."
      );

      return;
    }


    const numericAmount =
      Number(trimmedAmount);


    if (
      Number.isNaN(numericAmount) ||
      numericAmount <= 0
    ) {

      setValidationError(
        "Please enter a valid amount."
      );

      return;
    }


    // =======================================================
    // PAYLOAD
    // =======================================================

    const payload = {

      userId: trimmedUserId,

      amount: numericAmount,

    };


    console.log(
      "ADD SUPPORT INCOME PAYLOAD:",
      payload
    );


    // =======================================================
    // ADD SUPPORT INCOME
    // =======================================================

    try {

      setUpdating(true);


      const response =
        await dispatch(
          addSupportIncome(payload)
        ).unwrap();


      console.log(
        "ADD SUPPORT INCOME RESPONSE:",
        response
      );


      toast.success(
        response?.message ||
        "Support income added successfully"
      );


      // Go back after successful addition
      navigate(-1);


    } catch (err) {

      console.error(
        "ADD SUPPORT INCOME ERROR:",
        err
      );


      toast.error(
        typeof err === "string"
          ? err
          : err?.message ||
          "Failed to add support income"
      );


    } finally {

      setUpdating(false);

    }

  };


  // =========================================================
  // ERROR
  // =========================================================

  const errorMessage =
    validationError ||
    supportIncomeError ||
    error;


  // =========================================================
  // LOADING
  // =========================================================

  const isLoading =
    usersLoading ||
    supportIncomeLoading ||
    updating;


  // =========================================================
  // UI
  // =========================================================

  return (

    <div className="gi-referral-page">

      {/* =====================================================
          BREADCRUMB
      ===================================================== */}

      <div className="gi-breadcrumb">

        <Link to="/dashboard">

          <FiHome />

          Dashboard

        </Link>


        <FiChevronRight
          className="gi-breadcrumb-arrow"
        />


        <span
          onClick={() => navigate(-1)}
          style={{
            cursor: "pointer",
          }}
        >

          Admin Support

        </span>


        <FiChevronRight
          className="gi-breadcrumb-arrow"
        />


        <span>

          Add Support Income

        </span>

      </div>


      {/* =====================================================
          FORM CARD
      ===================================================== */}

      <section className="gi-transfer-card">


        {/* ===================================================
            LOADING USER
        =================================================== */}

        {usersLoading && !users?.length ? (

          <div className="gi-table-message">

            <FiLoader
              className="gi-transfer-spinner"
            />

            Loading user...

          </div>

        ) : (

          <form
            onSubmit={handleSubmit}
            className="gi-transfer-form"
          >


            {/* =================================================
                USER ID
            ================================================= */}

            <div className="gi-transfer-form-group">

              <label htmlFor="userId">

                User ID

                <span>*</span>

              </label>


              <div className="gi-transfer-input-wrapper">

                <FiUser />


                <input
                  id="userId"
                  type="text"
                  value={userId}
                  onChange={(e) => {

                    setUserId(
                      e.target.value
                    );

                    setValidationError("");

                  }}
                  placeholder="Enter user ID"
                  className="gi-transfer-input"
                  disabled={isLoading}
                  autoComplete="off"
                />

              </div>

            </div>


            {/* =================================================
                AMOUNT
            ================================================= */}

            <div className="gi-transfer-form-group">

              <label htmlFor="amount">

                Amount

                <span>*</span>

              </label>


              <div className="gi-transfer-input-wrapper">

                <FiCreditCard />


                <input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => {

                    const value =
                      e.target.value;

                    // Prevent negative values
                    if (
                      value === "" ||
                      Number(value) >= 0
                    ) {

                      setAmount(value);

                      setValidationError("");

                    }

                  }}
                  placeholder="Enter amount"
                  className="gi-transfer-input"
                  disabled={isLoading}
                  min="1"
                  step="0.01"
                  inputMode="decimal"
                />

              </div>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {errorMessage && (

              <div className="gi-transfer-error">

                <FiAlertCircle />

                <span>

                  {errorMessage}

                </span>

              </div>

            )}


            {/* =================================================
                SUBMIT
            ================================================= */}

            <button
              type="submit"
              className="gi-transfer-submit"
              disabled={isLoading}
            >

              {updating ? (

                <>

                  <FiLoader
                    className="gi-transfer-spinner"
                  />

                  Adding...

                </>

              ) : (

                <>

                  <FiSave />

                  Add Support Income

                </>

              )}

            </button>


          </form>

        )}

      </section>

    </div>

  );

};


export default AddSupportIncome;